import React, { memo, useCallback, useEffect, useState } from 'react'
import { getSongDetailAction } from '../store'
import { AppDispatch, RootState } from '@/store'
import { useDispatch, useSelector } from 'react-redux'
import { changeCurrentSongAction } from '../store/actionCreators'
import { changeCurrentLyricIndex, changeSequence } from '../store/reducer'
import { formatDate, getPlayUrl, getSizeImage } from '@/utils/format-utils'
import { NavLink } from 'react-router-dom'
import { message, Modal, Slider } from 'antd'
import AppPlayPanel from '../app-play-panel'
import { DEFAULT_SONG } from '@/common/contants'
import { PlaybarWrapper, ControlWrapper, PlayInfoWrapper, OperatorWrapper, ShareModalStyle, FloatingLyricWrapper } from './style'

/** 音乐播放器 */
export default memo(function AppPlayBar() {
    // 是否播放
    const [isPlaying, setIsPlaying] = useState(false)
    // 是否展示播放面板
    const [showPanel, setShowPanel] = useState(false)
    // 歌曲播放时间
    const [currentPlayTime, setCurrentPlayTime] = useState(0)
    // 进度条
    const [progress, setProgress] = useState(0)
    // 是否拖动歌曲进度条
    const [isDraging, setIsDraging] = useState(false)
    // 音量 (0-100)
    const [volume, setVolume] = useState(60)
    // 是否静音
    const [isMuted, setIsMuted] = useState(false)
    // 是否显示音量滑块
    const [showVolumeSlider, setShowVolumeSlider] = useState(false)
    // 是否已收藏
    const [isFavor, setIsFavor] = useState(false)
    // 是否显示分享弹窗
    const [showShareModal, setShowShareModal] = useState(false)
    // 是否显示悬浮歌词
    const [showFloatingLyric, setShowFloatingLyric] = useState(true)
    const [messageApi, contextHolder] = message.useMessage()

    const audioRef = React.useRef<HTMLAudioElement>(null)
    const dispatch = useDispatch<AppDispatch>()

    const { currentSong, sequence, lyricList, currentLyricIndex, playList } = useSelector((state: RootState) => ({
        currentSong: state.player.currentSong,
        sequence: state.player.sequence,
        lyricList: state.player.lyricList,
        currentLyricIndex: state.player.currentLyricIndex,
        playList: state.player.playList,
    }))

    const picUrl = React.useMemo(() => currentSong?.al?.picUrl || '', [currentSong])
    const songName = React.useMemo(() => currentSong?.name || '未知歌曲', [currentSong])
    const singerName = React.useMemo(() => currentSong.ar?.[0]?.name || '未知歌手', [currentSong])
    // 歌曲总时长
    const duration = React.useMemo(() => currentSong.dt || 0, [currentSong])
    const showDuration = formatDate(duration, 'mm:ss')
    const showCurrentTime = formatDate(currentPlayTime, 'mm:ss')
    // 当前歌词内容
    const currentLyricContent = React.useMemo(
        () => lyricList[currentLyricIndex]?.content || '',
        [lyricList, currentLyricIndex]
    )

    useEffect(() => {
        dispatch(getSongDetailAction(DEFAULT_SONG))
    }, [dispatch])

    useEffect(() => {
        // 播放器
        audioRef.current.src = getPlayUrl(currentSong.id)
        audioRef.current
            .play()
            .then(() => {
                setIsPlaying(true)
            })
            .catch(() => {
                setIsPlaying(false)
            })
    }, [currentSong])

    // 同步音量到 audio 元素
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.volume = isMuted ? 0 : volume / 100
        }
    }, [volume, isMuted])

    const changeMusic = tag => {
        dispatch(changeCurrentSongAction(tag))
    }

    // 拖动进度条
    const handleSliderChange = useCallback(
        (value: number) => {
            setIsDraging(true)
            const currentTime = (value / 100) * duration
            setCurrentPlayTime(currentTime)
            setProgress(value)
        },
        [duration]
    )

    // 拖动进度条完成
    const handleSliderCompleteChange = useCallback(
        (value: number) => {
            const currentTime = ((value / 100) * duration) / 1000

            audioRef.current.currentTime = currentTime
            setCurrentPlayTime(currentTime * 1000) // 在松开按钮那一瞬间设置时间，因为在timeUpdate中有延迟
            setIsDraging(false)
        },
        [duration]
    )

    // 切换播放模式
    const handleSequence = () => {
        const currentSequence = (sequence + 1) % 3
        dispatch(changeSequence(currentSequence))
    }

    // 暂停/播放音乐
    const playMusic = () => {
        isPlaying ? audioRef.current.pause() : audioRef.current.play()
        setIsPlaying(!isPlaying)
    }

    // 实时更新播放时间、进度、歌词
    const handleTimeUpdate = () => {
        const currentTime = audioRef.current.currentTime * 1000
        if (!isDraging) {
            setCurrentPlayTime(currentTime)
            setProgress((currentTime / duration) * 100)
        }

        // currentTime 已是毫秒，直接与歌词时间比较
        const index = lyricList.findIndex(lyric => currentTime < lyric.time)

        if (currentLyricIndex !== index - 1) {
            dispatch(changeCurrentLyricIndex(index - 1))
        }
    }

    // 歌曲播放完毕
    const handleMusicEnded = () => {
        if (sequence === 2) {
            // 若为单曲循环，则将进度条置为0，重新播放
            audioRef.current.currentTime = 0
            audioRef.current.play()
        } else {
            dispatch(changeCurrentSongAction(1))
        }
    }

    // 调节音量
    const handleVolumeChange = useCallback((value: number) => {
        setVolume(value)
        setIsMuted(value === 0)
    }, [])

    // 切换静音
    const toggleMute = () => {
        setIsMuted(!isMuted)
    }

    // 收藏歌曲
    const handleFavor = () => {
        const isLogin = localStorage.getItem('isLogin') === 'true'
        if (!isLogin) {
            messageApi.open({
                type: 'warning',
                content: '请先登录后再收藏',
                duration: 2,
            })
            return
        }
        setIsFavor(!isFavor)
        messageApi.open({
            type: 'success',
            content: isFavor ? '已取消收藏' : '收藏成功',
            duration: 1.5,
        })
    }

    // 分享
    const handleShare = () => {
        setShowShareModal(true)
    }

    // 复制分享链接
    const handleCopyLink = () => {
        const shareUrl = `${window.location.origin}/discover/player`

        // 优先使用 Clipboard API（需 HTTPS / localhost）
        if (navigator.clipboard?.writeText) {
            navigator.clipboard
                .writeText(shareUrl)
                .then(() => messageApi.open({ type: 'success', content: '链接已复制到剪贴板', duration: 1.5 }))
                .catch(() => fallbackCopy(shareUrl))
        } else {
            fallbackCopy(shareUrl)
        }
    }

    // 兼容非安全上下文（HTTP）的复制方案
    const fallbackCopy = (text: string) => {
        const textarea = document.createElement('textarea')
        textarea.value = text
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        document.body.appendChild(textarea)
        textarea.select()
        try {
            document.execCommand('copy')
            messageApi.open({ type: 'success', content: '链接已复制到剪贴板', duration: 1.5 })
        } catch {
            messageApi.open({ type: 'error', content: '复制失败，请手动复制', duration: 1.5 })
        } finally {
            document.body.removeChild(textarea)
        }
    }

    return (
        <PlaybarWrapper className="sprite_playbar">
            {contextHolder}
            <ShareModalStyle />
            {/* 悬浮歌词 */}
            {showFloatingLyric && currentLyricContent && (
                <FloatingLyricWrapper>
                    <span className="lyric-text">{currentLyricContent}</span>
                    <button className="lyric-close" onClick={() => setShowFloatingLyric(false)}>
                        ×
                    </button>
                </FloatingLyricWrapper>
            )}
            <div className="content wrap-v2">
                {/* 上一首/播放/下一首 */}
                <ControlWrapper $isPlaying={isPlaying}>
                    <button className="sprite_playbar btn prev" title="上一曲" onClick={() => changeMusic(-1)}></button>
                    <button className="sprite_playbar btn play" title={isPlaying ? '暂停' : '播放'} onClick={() => playMusic()}></button>
                    <button className="sprite_playbar btn next" title="下一曲" onClick={() => changeMusic(1)}></button>
                </ControlWrapper>
                {/* 当前播放歌曲信息 */}
                <PlayInfoWrapper>
                    {/* 歌曲封面 */}
                    <div className="image">
                        <NavLink to="/discover/player">
                            <img src={getSizeImage(picUrl, 35)} alt="" />
                        </NavLink>
                    </div>
                    <div className="info">
                        <div className="song">
                            <span className="song-name">{songName}</span>
                            <span className="singer-name">{singerName}</span>
                        </div>
                        {/* 进度条 */}
                        <div className="progress">
                            <Slider
                                value={progress}
                                onChange={handleSliderChange}
                                onChangeComplete={handleSliderCompleteChange}
                            />
                            <div className="time">
                                <span className="now-time">{showCurrentTime}</span>
                                <span className="divider">/</span>
                                <span className="total-time">{showDuration}</span>
                            </div>
                        </div>
                    </div>
                </PlayInfoWrapper>
                <OperatorWrapper $sequence={sequence}>
                    <div className="left">
                        {/* 喜欢 */}
                        <button
                            className={`sprite_playbar btn favor ${isFavor ? 'active' : ''}`}
                            title="收藏"
                            onClick={() => handleFavor()}
                        ></button>
                        {/* 分享 */}
                        <button
                            className="sprite_playbar btn share"
                            title="分享"
                            onClick={() => handleShare()}
                        ></button>
                        {/* 悬浮歌词开关 */}
                        <button
                            className={`lyric-toggle ${showFloatingLyric ? 'active' : ''}`}
                            onClick={() => setShowFloatingLyric(!showFloatingLyric)}
                            title="歌词"
                        >
                            词
                        </button>
                    </div>
                    <div className="right sprite_playbar">
                        {/* 音量 */}
                        <div
                            className="volume-wrapper"
                            onMouseEnter={() => setShowVolumeSlider(true)}
                            onMouseLeave={() => setShowVolumeSlider(false)}
                        >
                            <button
                                className={`sprite_playbar btn volume ${isMuted ? 'muted' : ''}`}
                                title={isMuted ? '取消静音' : '静音'}
                                onClick={() => toggleMute()}
                            ></button>
                            {showVolumeSlider && (
                                <div className="volume-slider">
                                    <div className="slider-inner">
                                        <Slider
                                            vertical
                                            value={isMuted ? 0 : volume}
                                            onChange={handleVolumeChange}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                        {/* 播放模式 */}
                        <button
                            className="sprite_playbar btn loop"
                            title={sequence === 1 ? '随机播放' : sequence === 2 ? '单曲循环' : '循环播放'}
                            onClick={() => handleSequence()}
                        ></button>
                        {/* 播放列表 */}
                        <button
                            className="sprite_playbar btn playlist"
                            title="播放列表"
                            onClick={() => setShowPanel(!showPanel)}
                        ></button>
                        <span className="playlist-num">{playList.length}</span>
                    </div>
                </OperatorWrapper>
                <audio ref={audioRef} onTimeUpdate={handleTimeUpdate} onEnded={e => handleMusicEnded()} />
                {showPanel && <AppPlayPanel />}
            </div>
            {/* 分享弹窗 */}
            <Modal
                title="分享歌曲"
                open={showShareModal}
                onCancel={() => setShowShareModal(false)}
                footer={null}
                width={420}
            >
                <div className="share-modal">
                    <div className="share-song-info">
                        <img src={getSizeImage(picUrl, 80)} alt="" />
                        <div className="share-song-text">
                            <div className="share-song-name">{songName}</div>
                            <div className="share-song-singer">{singerName}</div>
                        </div>
                    </div>
                    <div className="share-platforms">
                        <button className="share-item" onClick={handleCopyLink}>
                            <span className="share-icon link">link</span>
                            <span>复制链接</span>
                        </button>
                    </div>
                </div>
            </Modal>
        </PlaybarWrapper>
    )
})
